import React, {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {RootState} from '../../../redux/store'
import {useInterpreters} from '../../../hooks/useGlobalData'
import {DashboardFilterBar} from '../components/DashboardFilterBar/DashboardFilterBar'
import {toFilterOptions} from '../components/DashboardFilterBar/utils'
import type {FilterFieldConfig} from '../components/DashboardFilterBar/types'
import type {IpiVisFilters} from './_requests'

type Props = {
  onApply: (filters: IpiVisFilters) => void
  isLoading: boolean
  defaultStartDate: string
  defaultEndDate: string
}

export const IpiVisFilterBar: React.FC<Props> = ({
  onApply,
  isLoading,
  defaultStartDate,
  defaultEndDate,
}) => {
  const {items: customers, loading: isFetchingCustomer} = useSelector(
    (state: RootState) => state.customers
  )
  const {items: languages, loading: isFetchingLanguage} = useSelector(
    (state: RootState) => state.languages
  )
  const {data: interpreters, isLoading: isFetchingInterpreter} = useInterpreters()

  const fields: FilterFieldConfig[] = useMemo(
    () => [
      {
        id: 'languages',
        iconPath: '/media/icons/ipivis/language.svg',
        placeholder: 'All Languages',
        options: toFilterOptions(languages),
        isLoading: isFetchingLanguage,
      },
      {
        id: 'interpreters',
        iconPath: '/media/icons/ipivis/interpreter.svg',
        placeholder: 'All Interpreters',
        options: toFilterOptions(interpreters),
        isLoading: isFetchingInterpreter,
      },
      {
        id: 'customers',
        iconPath: '/media/icons/ipivis/customer.svg',
        placeholder: 'All Customers',
        options: toFilterOptions(customers),
        isLoading: isFetchingCustomer,
      },
    ],
    [
      languages,
      interpreters,
      customers,
      isFetchingLanguage,
      isFetchingInterpreter,
      isFetchingCustomer,
    ]
  )

  return (
    <DashboardFilterBar
      fields={fields}
      defaultStartDate={defaultStartDate}
      defaultEndDate={defaultEndDate}
      isLoading={isLoading}
      onApply={({startDate, endDate, selections}) =>
        onApply({
          startDate,
          endDate,
          languages: selections.languages ?? [],
          interpreters: selections.interpreters ?? [],
          customers: (selections.customers ?? [])
            .map((v) => Number(v))
            .filter((n) => !Number.isNaN(n)),
        })
      }
    />
  )
}
